import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Input, Select } from 'components/FormFields';
import PageHeaderIcon from 'components/PageHeaderIcon';
import { POSButton } from 'components/POS';
import { PointOfSaleSettings } from 'pages/app/Accounts/AddAccounts/components/PointOfSaleSettings';
import { useGetEvent } from 'pages/app/Events/EventsMenu/gql/queries/get-event';
import { errorMessage, successMessage } from 'shared/alerts';
import { useCart } from 'shared/CartContext';
import { validateIpAddress } from 'shared/utils/ValidateIp';
import { useUpdatePOSSettings } from './gql/mutation/useUpdatePosSettings';
import { useGetAccountPosSettings } from './gql/queries/useGetAccountPosSettings';
import { useGetReaders } from './gql/queries/useGetReaders';

const PageContainer = styled.div`
  padding: 20px;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.primary ||
        'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)'
      : '#f8fafc'};
`;

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const ContentCard = styled(Card)`
  padding: 0;
`;

const ContentWrapper = styled.div`
  padding: 24px;
`;

const DeviceSettingsCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
`;

const DeviceCardHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'};
`;

const DeviceCardTitle = styled.h3`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

const DeviceCardDescription = styled.p`
  font-size: 13px;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.muted || '#64748B'
      : '#64748B'};
`;

const DeviceInputsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const formValues = ['pos_settings'];

export const PosSettings = () => {
  const { id } = useParams();

  const { accountId } = useCart();

  const history = useHistory();

  const location = useLocation();
  const { event } = location.state || {};

  const [ipAddress, setIpAddress] = useState('');
  const [isValidIp, setIsValidIp] = useState(true);

  const [selectedReader, setSelectedReader] = useState('');

  const handleInputChange = (e) => {
    const value = e.target.value;
    setIpAddress(value);

    // Validate IP address
    setIsValidIp(validateIpAddress(value));
  };

  const updatePOSSettings = useUpdatePOSSettings();

  const { data } = useGetEvent(parseInt(id), !!event);

  const currentEvent = event ?? data?.getEvent;

  const { data: accountData } = useGetAccountPosSettings(
    currentEvent?.account_id,
    !!currentEvent.pos_settings
  );

  const accountPosSettings = accountData?.getAccount;

  // Get readers for the account
  const { data: readersData } = useGetReaders(
    accountId ? accountId : currentEvent?.account_id,
    true
  );
  const readers = readersData?.getAccount?.readers || [];
  const readerOptions = readers.map((reader) => ({
    label: `${reader.label} - ${reader.reader_id}`,
    value: reader.reader_id,
  }));

  // Load saved reader from cookie
  useEffect(() => {
    if (readers.length > 0) {
      const readerCookie = document.cookie
        .split('; ')
        .find((row) =>
          row.startsWith(
            `${accountId ? accountId : currentEvent?.account_id}-reader=`
          )
        );

      if (readerCookie) {
        const readerId = readerCookie.split('=')[1];
        if (readers.find((r) => r.reader_id === readerId)) {
          setSelectedReader(readerId);
        }
      }
    }
  }, [accountId, currentEvent.account_id, readers]);

  function getInitialValues(event, keys) {
    const blankValues = {
      ...keys.reduce((values, key) => ({ ...values, [key]: '' }), {}),
    };

    const initialValues = !event
      ? blankValues
      : {
          ...keys.reduce((formValues, key) => {
            return event[key]
              ? {
                  ...formValues,
                  [key]: event[key],
                }
              : formValues;
          }, {}),
        };

    return initialValues;
  }

  function getPOSSettingsInput({ data }) {
    const input = {
      ...data,
    };
    return input;
  }

  const UpdateIpAddress = async () => {
    // Save the input IP address to local storage if valid
    if (isValidIp) {
      localStorage.setItem('readerIp', ipAddress);
    }
  };

  const SaveReader = async () => {
    // Save the selected reader id to cookies with an expiration time of 12 hours
    if (selectedReader) {
      document.cookie = `${
        accountId ? accountId : currentEvent?.account_id
      }-reader=${selectedReader}; expires=${new Date(
        new Date().getTime() + 12 * 60 * 60 * 1000
      ).toUTCString()}; path=/`;
    }
  };

  const pathname = useLocation().pathname;
  const userType =
    ['admin-employee', 'admin-track'].find((type) => pathname.includes(type)) ||
    'admin';

  const openCashDrawerReport = () =>
    history.push(`/${userType}/events/cashDrawerReport/${event.id}`, {
      event: event,
    });

  // Check if the IP address is saved in local storage
  useEffect(() => {
    const savedIpAddress = localStorage.getItem('readerIp');
    if (savedIpAddress) {
      setIpAddress(savedIpAddress);
    }
  }, []);

  return (
    <EnhancedThemeProvider>
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValues(
          currentEvent ?? accountPosSettings,
          formValues
        )}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
          setSubmitting(true);

          const data = getPOSSettingsInput({
            data: values,
          });

          formValues.forEach((key) => {
            if (data[key] === '' || data[key] === undefined) delete data[key];
          });

          if (data['pos_settings']) {
            delete data[`pos_settings`]['__typename'];
          }

          const response = await updatePOSSettings({
            event_id: +id,
            pos_settings: data.pos_settings,
          });

          if (!response || response.errors) {
            errorMessage(response);
            setSubmitting(false);
            return setErrors(response.errors);
          } else {
            // Update IP address value
            UpdateIpAddress();

            // Save selected reader
            if (selectedReader) {
              SaveReader();
            }

            successMessage('POS Settings Updated');
            setSubmitting(false);
            resetForm();
            history.goBack();
          }

          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <PageContainer>
            <form onSubmit={handleSubmit}>
              {/* Header Card */}
              <HeaderCard>
                <HeaderContainer>
                  <HeaderLeft>
                    <PageHeaderIcon icon="settings" />
                    <HeaderTitle>POS Settings</HeaderTitle>
                  </HeaderLeft>

                  <ButtonContainer>
                    <POSButton
                      type="button"
                      variant="outlined"
                      onClick={openCashDrawerReport}
                      disabled={isSubmitting}
                      style={{ minWidth: 180 }}
                    >
                      My Box Office Report
                    </POSButton>

                    <POSButton
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                      style={{ minWidth: 120 }}
                    >
                      Save
                    </POSButton>
                  </ButtonContainer>
                </HeaderContainer>
              </HeaderCard>

              {/* Settings Content */}
              <ContentCard noPadding>
                <ContentWrapper>
                  <PointOfSaleSettings
                    handleChange={(target) => {
                      const { name, value } = target.target;

                      const newSettings = {
                        ...values.pos_settings,
                        [name]: value,
                      };

                      typeof name === 'string' &&
                        handleChange({
                          target: {
                            name: 'pos_settings',
                            value: newSettings,
                          },
                        });
                    }}
                    mainValues={values}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                  />

                  {/* Device Settings */}
                  <DeviceSettingsCard>
                    <DeviceCardHeader>
                      <DeviceCardTitle>Device Settings</DeviceCardTitle>
                      <DeviceCardDescription>
                        Configure your POS hardware and reader settings
                      </DeviceCardDescription>
                    </DeviceCardHeader>
                    <DeviceInputsContainer>
                      <Input
                        name="ipAddress"
                        label="IP Address"
                        placeholder="xxx.xxx.xxx.xxx"
                        required
                        onChange={handleInputChange}
                        value={ipAddress}
                        error={!isValidIp && ipAddress !== ''}
                        helperText={
                          !isValidIp && ipAddress !== ''
                            ? 'Invalid IP address'
                            : ''
                        }
                      />

                      <Select
                        name="reader"
                        label="Select Reader"
                        placeholder="Choose a reader"
                        options={readerOptions}
                        value={selectedReader}
                        onChange={(e) => setSelectedReader(e.target.value)}
                      />
                    </DeviceInputsContainer>
                  </DeviceSettingsCard>
                </ContentWrapper>
              </ContentCard>
            </form>
          </PageContainer>
        )}
      </Formik>
    </EnhancedThemeProvider>
  );
};
